import { useState } from 'react';
import { useT } from '@/store';
import { useMaterials } from '@/hooks/useDirectory';
import { useDeleteMaterial, useSaveMaterial } from '@/hooks/useDataAdmin';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';

/**
 * The material catalogue — the controlled vocabulary behind cargo types and
 * quarry prices. Small on purpose: its whole job is to stop "Skalda 0/32",
 * "skalda 0-32" and "Skalda 0/32 " existing as three different priced
 * materials that no order matches.
 *
 * V-229. Archived materials are fetched and shown here (dimmed, with a
 * restore action) rather than hidden: schema.sql's UNIQUE (tenant_id, name)
 * covers them too, so a name that looks free because its old row is
 * archived is not actually free — the backend now says so with a clear
 * 409 (err.material_name_belongs_to_archived) instead of a raw
 * constraint error, and this is where that name's owner can be found and
 * brought back, the same way VehicleGrid.jsx already does for plates.
 */
export function MaterialList() {
  const t = useT();
  const { data: materials = [], isLoading, isError, error } = useMaterials({ includeInactive: true });
  const saveMaterial = useSaveMaterial();
  const deleteMaterial = useDeleteMaterial();
  const [name, setName] = useState('');

  const add = () => {
    if (!name.trim()) return;
    saveMaterial.mutate({ name: name.trim() }, { onSuccess: () => setName('') });
  };

  return (
    <Panel title={t('data.materialsHeading')}>
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={3} />
      ) : materials.length === 0 ? (
        <EmptyState>{t('data.noMaterialsYet')}</EmptyState>
      ) : (
        <ul className="mb-3 flex flex-wrap gap-1.5">
          {materials.map((material) => (
            <li
              key={material.material_id}
              className={`flex items-center gap-1.5 rounded-full border border-line py-1 pl-3 pr-1 text-[12px] ${
                material.active ? 'bg-white/3' : 'bg-transparent opacity-60'
              }`}
            >
              {material.name}
              {material.active ? null : <ArchivedBadge deletedAt={material.deleted_at} />}
              {material.active ? (
                <ConfirmButton
                  label="✕"
                  confirmLabel={t('common.confirmDelete')}
                  onConfirm={() => deleteMaterial.mutate(material.material_id)}
                  className="!rounded-full"
                />
              ) : (
                <button
                  type="button"
                  className="btn !rounded-full !px-2 !py-0.5 !text-[11px]"
                  disabled={saveMaterial.isPending}
                  onClick={() => saveMaterial.mutate({ materialId: material.material_id, active: true })}
                >
                  {t('common.restoreBtn')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <input
          className="field"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder={t('data.materialNamePh')}
        />
        <button type="button" className="btn btn-primary" onClick={add} disabled={saveMaterial.isPending}>
          {t('common.addBtn')}
        </button>
      </div>

      {saveMaterial.isError ? <ErrorState error={saveMaterial.error} /> : null}
      {deleteMaterial.isError ? <ErrorState error={deleteMaterial.error} /> : null}
    </Panel>
  );
}
